import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./features/auth/hooks/useAuth";
import Loading from "./components/Loading";

export const LOGIN_ROUTE = "/login";
export const SIGNUP_ROUTE = "/signup";
export const LANDING_ROUTE = "/landing";
export const DOWNLOAD_APP_ROUTE = "/download";
export const FORGOT_PWD_ROUTE = "/forgot-password";
export const PASSWORD_RECOVERY_ROUTE = "/password-recovery/:token";
export const EVENTS_ROUTE = "/events";
export const CALENDAR_ROUTE = "/calendar";
export const CREATE_EVENT_ROUTE = "/events/create";
export const UPDATE_EVENT_ROUTE = "/events/:eventId/edit";
export const EVENT_APPROVED_INVITE_ROUTE = "/events/:eventId/approved-invite";
export const EVENT_ATTENDANCE_ROUTE = "/events/:eventId/attendance";
// Landing page for the `gallery_photo_added` push (P14). The backend already
// deep-links here: see notify_gallery_photo_added.go.
export const GALLERY_ROUTE = "/gallery/:eventId";
export const galleryPath = (eventId: string) => `/gallery/${eventId}`;
export const WELCOME_ROUTE = "/welcome";
export const CONFIRM_MAIL_ROUTE = "/confirm/:confirmToken";
export const REENGAGEMENT_ROUTE = "/reengagement/:token";
export const SIGNOUT_ROUTE = "/signout";
export const PROFILE_ROUTE = "/profile";
export const ACCOUNT_ROUTE = "/profile/account";
export const MY_EVENTS_ROUTE = "/profile/myevents";
export const PAYMENT_METHODS_ROUTE = "/profile/payment-methods";
export const RELIABILITY_HISTORY_ROUTE = "/profile/reliability-history";
export const EXTRA_ROUTE = "/extra";
export const FOLLOWERS_ROUTE = "/profile/requests/followers";
export const USERS_MET_ROUTE = "/profile/requests/users-met";

export const SUBSCRIPTIONS_PLANS_ROUTE = "/subscriptions/plans";
export const SUBSCRIPTION_ACTIVATION_ROUTE = "/subscription/activation";

export const COOKIE_POLICY_ROUTE = "/cookie-policy";
export const PRIVACY_POLICY_ROUTE = "/privacy-policy";
export const CHATS_ROUTE = "/chats";
export const CHAT_INVITE_ROUTE = "/chat/invite/:token";
export const TERMS_ROUTE = "/terms";
export const FAQ_ROUTE = "/faq";
export const GIOJOY_NEWS_ROUTE = "/giojoy-news";
export const AD_EXAMPLES_ROUTE = "/ad-examples"; // Test route for AdSense examples

// Purchase flow routes
export const CHECKOUT_ROUTE = "/checkout";
export const PURCHASED_ROUTE = "/purchased";

// Subscription management routes
export const UPDATE_SUBSCRIPTION_ROUTE = "/events/:eventId/update-subscription";

// Stripe onboarding routes
export const ONBOARDING_ROUTE = "/seller/onboarding";
export const ONBOARDING_RESULT_ROUTE = "/seller/onboarding/result";

// Admin routes
export const ADMIN_ROUTE = "/admin";
export const ADMIN_USERS_ROUTE = "/admin/users";
export const ADMIN_EVENTS_ROUTE = "/admin/events";
export const ADMIN_EVENT_EDIT_ROUTE = "/admin/events/:eventId/edit";
export const ADMIN_CHATS_ROUTE = "/admin/chats";
export const ADMIN_CHAT_SETTINGS_ROUTE = "/admin/chat-settings";
export const ADMIN_SUBSCRIPTIONS_ROUTE = "/admin/subscriptions";
export const ADMIN_ORDERS_ROUTE = "/admin/orders";
export const ADMIN_FEES_ROUTE = "/admin/fees";
export const ADMIN_PLANS_ROUTE = "/admin/plans";
export const ADMIN_PLAN_EDIT_ROUTE = "/admin/plans/:planId/edit";
export const ADMIN_WITHDRAWALS_ROUTE = "/admin/withdrawals";
export const ADMIN_ADVERTISING_ROUTE = "/admin/advertising";
export const ADMIN_QUOTES_ROUTE = "/admin/quotes";
export const ADMIN_CRM_ROUTE = "/admin/crm";
export const ADMIN_PROMOS_ROUTE = "/admin/promos";
export const ADMIN_CRM_NEW_ROUTE = "/admin/crm/new";
export const ADMIN_CRM_EDIT_ROUTE = "/admin/crm/:campaignId/edit";
export const ADMIN_CHAT_REPORTS_ROUTE = "/admin/chat-reports";
export const ADMIN_BLOCKED_USERS_ROUTE = "/admin/blocked-users";
export const ADMIN_SETTINGS_ROUTE = "/admin/settings";

interface ProtectedRouteProps {
  component: React.ComponentType;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  component: Component,
}) => {
  const { isAuthenticated, authInitialized } = useAuth();
  const location = useLocation();

  if (!authInitialized) {
    // You could return a loading spinner here
    return <Loading />;
  }

  if (!isAuthenticated) {
    console.log("User is not authenticated. Redirecting to login.");
    // Encode current path and append as redirectTo parameter
    const redirectUrl = `${LOGIN_ROUTE}?redirectTo=${encodeURIComponent(
      location.pathname
    )}`;
    return <Navigate to={redirectUrl} />;
  }

  return <Component />;
};

interface AdminRouteProps {
  component: React.ComponentType;
}

export const AdminRoute: React.FC<AdminRouteProps> = ({
  component: Component,
}) => {
  const { isAuthenticated, authInitialized, hasRole } = useAuth();
  const location = useLocation();

  if (!authInitialized) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    console.log("User is not authenticated. Redirecting to login.");
    const redirectUrl = `${LOGIN_ROUTE}?redirectTo=${encodeURIComponent(
      location.pathname
    )}`;
    return <Navigate to={redirectUrl} />;
  }

  if (!hasRole("admin")) {
    console.log("User does not have admin role. Redirecting to events.");
    return <Navigate to={EVENTS_ROUTE} />;
  }

  return <Component />;
};
